/**
 * The page <head> - the title and the favicon.
 *
 * This replaces vue-meta 2, which was vue 2 only and could not be carried over by
 * @vue/compat: it collected `metaInfo` by walking the component tree through `$children`
 * and reached into instance internals besides, from a global mixin that ran on every
 * component. Under vue 3 that threw on every refresh, and because the throws happened
 * inside lifecycle hooks they took the mounting component down with them - which is why
 * modals stopped opening.
 *
 * The app only ever used three of vue-meta's fields, so this implements those three and
 * keeps the `metaInfo` option name: no page component changes.
 *
 *   metaInfo () {
 *       return {
 *           title: 'My Goals',                 // deepest component on the page wins
 *           titleTemplate: '%s - Multi Me',    // likewise, and %s is the title
 *           link: [{rel: 'icon', href: ..., vmid: 'favicon'}],  // merged by vmid
 *       };
 *   }
 *
 * Nothing walks the tree here. Each component with a `metaInfo` registers itself and
 * watches its own metaInfo for changes, so a re-render only costs the components whose
 * head actually depends on what changed.
 */

// the mounted components that carry a metaInfo, mapped to the stop function of the watch
// that re-applies when their own metaInfo changes. In no particular order - depth decides
// precedence, and it is read at apply time because a component can move in the tree
const contributors = new Map();

// the link elements this module owns, by vmid, so they can be updated in place rather
// than piled up on every navigation
const managedLinks = new Map();

let applyScheduled = false;

function depthOf (vm) {
    let depth = 0;
    for (let parent = vm.$parent; parent; parent = parent.$parent) {
        depth += 1;
    }
    return depth;
}

function readMetaInfo (vm) {
    const metaInfo = vm.$options.metaInfo;
    if (!metaInfo) {
        return null;
    }
    if (typeof metaInfo !== 'function') {
        return metaInfo;
    }
    try {
        return metaInfo.call(vm);
    } catch (e) {
        // a metaInfo reads the page's own data, and a page can lose that data while it is
        // still a contributor - it used to be GoalDetailPageMain, which unregistered the vuex
        // module its metaInfo read from. It has nothing left to say about the head either
        // way, and one component in that state must not take the whole update down with it -
        // `apply` runs off a promise, so the throw came out as an unhandled rejection and no
        // title was set
        return null;
    }
}

function applyTitle (title, titleTemplate) {
    if (title === undefined || title === null) {
        // nothing on the page claimed a title, so leave whatever the django template
        // rendered - blanking it would be worse than being out of date
        return;
    }
    document.title = titleTemplate ? String(titleTemplate).replace('%s', title) : title;
}

function applyLinks (links) {
    for (const [vmid, link] of links) {
        let el = managedLinks.get(vmid);
        if (!el) {
            el = document.createElement('link');
            el.setAttribute('data-vue-meta', vmid);
            document.head.appendChild(el);
            managedLinks.set(vmid, el);
        }
        // rewrite the attributes rather than the element: a favicon that is replaced
        // rather than updated makes some browsers flash the default one
        for (const attr of [...el.attributes]) {
            if (attr.name !== 'data-vue-meta' && !(attr.name in link)) {
                el.removeAttribute(attr.name);
            }
        }
        for (const [name, value] of Object.entries(link)) {
            if (name !== 'vmid' && value !== undefined && value !== null) {
                el.setAttribute(name, value);
            }
        }
    }

    for (const [vmid, el] of managedLinks) {
        if (!links.has(vmid)) {
            el.remove();
            managedLinks.delete(vmid);
        }
    }
}

function apply () {
    applyScheduled = false;

    // shallowest first, so a deeper component's title overwrites its ancestors' - the
    // ordering vue-meta had, and what SpaRoot (the section's default head) relies on to
    // sit underneath the page component that names the page
    const heads = [...contributors.keys()]
        .map(vm => ({depth: depthOf(vm), info: readMetaInfo(vm)}))
        .filter(entry => entry.info)
        .sort((a, b) => a.depth - b.depth);

    let title;
    let titleTemplate;
    const links = new Map();

    for (const {info} of heads) {
        if (info.title !== undefined) {
            title = info.title;
        }
        if (info.titleTemplate !== undefined) {
            titleTemplate = info.titleTemplate;
        }
        for (const link of info.link || []) {
            links.set(link.vmid || `${link.rel}:${link.href}`, link);
        }
    }

    applyTitle(title, titleTemplate);
    applyLinks(links);
}

function scheduleApply () {
    if (applyScheduled) {
        return;
    }
    applyScheduled = true;
    // coalesce: a navigation unmounts one page and mounts the next, and every component
    // between them would otherwise write the title in turn
    Promise.resolve().then(apply);
}

const VueMetaPlugin = {
    install (app) {
        app.mixin({
            created () {
                if (!this.$options.metaInfo) {
                    return;
                }
                // re-apply when what the metaInfo reads changes - $route, a loaded
                // object's name - instead of on every re-render of every component
                contributors.set(this, this.$watch(() => readMetaInfo(this), scheduleApply));
            },
            mounted () {
                if (this.$options.metaInfo) {
                    scheduleApply();
                }
            },
            /**
             * Leaving here rather than in `unmounted`.
             *
             * A page can tear down the state its metaInfo reads in its own
             * `beforeUnmount` - a `$reset()` on the page's store - and vue keeps the watch
             * alive until the instance's scope stops, which is later still. A global
             * mixin's hooks run before the component's own, so this is the last moment at
             * which the metaInfo is guaranteed readable.
             */
            beforeUnmount () {
                const unwatch = contributors.get(this);
                if (!unwatch) {
                    return;
                }
                unwatch();
                contributors.delete(this);
                scheduleApply();
            },
        });
    },
};

export default VueMetaPlugin;
export {VueMetaPlugin, apply as applyMetaNow, contributors as metaContributors};
